import { AddressAutofill } from "@mapbox/search-js-react";
import { Select, SelectItem } from "components/Select";
import type { FC } from "react";
import { CircleFlag } from "react-circle-flags";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import { AccordionItem, Badge, CmsError, Heading, Input } from "src/components";
import { CloseIcon } from "src/icons";
import { useCountriesQuery } from "src/serverApi";
import { type User, UserVerificationTypeEnum } from "src/types";
import { getAmlFormScheme } from "./AmlFormScheme";
import { QuestionnaireSection } from "./enums";
import { getLocalizedRegionNameFromCountryCode } from "./utils";

type PersonalInfoProps = Readonly<{
    user: User;
}>;

const CZ = "CZ";
const SK = "SK";

export const PersonalInfo: FC<PersonalInfoProps> = ({ user }) => {
    const { t } = useTranslation();

    const { data: countries, isLoading: isCountriesLoading, error: countriesError, isError: isCountriesError } = useCountriesQuery();

    const schema = getAmlFormScheme(t);
    type FormData = z.infer<typeof schema>;

    const localizedCountries = (countries ?? [])
        .map((country) => {
            return {
                ...country,
                name: getLocalizedRegionNameFromCountryCode(country.alpha2) ?? country.alpha3,
            };
        })
        .sort((a, b) => {
            // Czech Republic first, Slovakia second and the rest alphabetically
            if (a.alpha2.toUpperCase() === SK) {
                return b.alpha2.toUpperCase() === CZ ? 1 : -1;
            }
            if (b.alpha2.toUpperCase() === SK) {
                return a.alpha2.toUpperCase() === CZ ? -1 : 1;
            }
            if (a.alpha2.toUpperCase() === CZ || a.alpha2.toUpperCase() === SK) return -1;
            if (b.alpha2.toUpperCase() === CZ || b.alpha2.toUpperCase() === SK) return 1;

            return a.name.localeCompare(b.name);
        });

    const {
        register,
        control,
        formState: { errors },
    } = useFormContext<FormData>();

    return (
        <AccordionItem
            value={QuestionnaireSection.PersonalInfo}
            className="my-6 overflow-hidden rounded border bg-white p-6"
            title={
                user?.verification.some((verification) => verification.type === UserVerificationTypeEnum.KYC) ? (
                    <div className="flex items-center gap-x-3">
                        <Heading level={3} className="max-sm:text-left">
                            {t("verification.AML.personalInfo.heading.verified.title")}
                        </Heading>
                        <Badge intent="subtleSuccess" className="text-sm">
                            {t("verification.AML.personalInfo.heading.verified.badge")}
                        </Badge>
                    </div>
                ) : (
                    <Heading level={3}>{t("verification.AML.personalInfo.heading.notVerified.title")}</Heading>
                )
            }
            triggerIcon={<CloseIcon className="size-8 transition-transform duration-300" />}
            content={
                <div className="flex flex-col gap-y-4 py-8">
                    {user?.userProfile && (
                        <p className="text-left text-sm text-secondary">{t("verification.AML.personalInfo.heading.verified.note")}</p>
                    )}
                    <div className="flex w-full flex-col gap-x-5 gap-y-2 sm:flex-row">
                        <Input
                            type="text"
                            {...register("firstName")}
                            label={t("verification.AML.personalInfo.inputs.firstName.label")}
                            error={errors.firstName}
                            disabled={!!user?.userProfile?.firstName}
                        />
                        <Input
                            type="text"
                            {...register("lastName")}
                            label={t("verification.AML.personalInfo.inputs.lastName.label")}
                            error={errors.lastName}
                            disabled={!!user?.userProfile?.lastName}
                        />
                    </div>
                    <Controller
                        name="residentialAddress"
                        control={control}
                        render={({ field }) => (
                            // Incompatible typings with typescript 5
                            // @ts-expect-error
                            <AddressAutofill accessToken={import.meta.env.VITE_MAPBOX_TOKEN}>
                                <Input
                                    {...field}
                                    ref={field.ref}
                                    type="text"
                                    label={t("verification.AML.personalInfo.inputs.address.label")}
                                    error={errors.residentialAddress}
                                    autoComplete="address-line1"
                                />
                            </AddressAutofill>
                        )}
                    />
                    <Input
                        type="text"
                        {...register("cityAddress")}
                        label={t("verification.AML.personalInfo.inputs.addressCity.label")}
                        error={errors.cityAddress}
                        autoComplete="address-level2"
                    />
                    <Input
                        type="text"
                        {...register("postalCode")}
                        label={t("verification.AML.personalInfo.inputs.addressPostalCode.label")}
                        error={errors.postalCode}
                        autoComplete="postal-code"
                    />
                    <Controller
                        name="countryAddress"
                        control={control}
                        render={({ field }) => (
                            <>
                                <Select
                                    {...field}
                                    label={t("verification.AML.personalInfo.inputs.addressCountry.label")}
                                    error={errors.countryAddress}
                                    disabled={isCountriesError || isCountriesLoading}
                                >
                                    {(localizedCountries ?? []).map(({ alpha2, alpha3, name }) => (
                                        <SelectItem key={alpha3} value={alpha3}>
                                            <div className="flex items-center gap-2">
                                                <CircleFlag countryCode={alpha2.toLowerCase()} className="w-[18px]" />
                                                {name}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </Select>
                                {isCountriesError && <CmsError error={countriesError} fallbackMessage={t("common.error")} />}
                            </>
                        )}
                    />
                    <div className="flex w-full flex-col gap-x-5 gap-y-2 sm:flex-row">
                        <Input
                            type="date"
                            {...register("dateOfBirth")}
                            label={t("verification.AML.personalInfo.inputs.dateOfBirth.label")}
                            error={errors.dateOfBirth}
                            disabled={!!user?.userProfile?.dateOfBirth}
                        />
                        <Input
                            type="text"
                            {...register("socialSecurityNumber")}
                            label={t("verification.AML.personalInfo.inputs.socialSecurityNumber.label")}
                            error={errors.socialSecurityNumber}
                            optional
                            disabled={!!user?.userProfile?.socialSecurityNumber}
                        />
                    </div>
                    <Input
                        type="text"
                        {...register("idOrPassportNumber")}
                        label={t("verification.AML.personalInfo.inputs.idPassportNumber.label")}
                        error={errors.idOrPassportNumber}
                        disabled={!!user?.userProfile?.idOrPassportNumber}
                    />
                    <Input
                        type="text"
                        {...register("countryOfOrigin")}
                        label={t("verification.AML.personalInfo.inputs.countryOfOrigin.label")}
                        additionalInfo={t("verification.AML.personalInfo.inputs.countryOfOrigin.additionalInfo")}
                        error={errors.countryOfOrigin}
                    />
                    <Input
                        type="text"
                        {...register("citizenship")}
                        label={t("verification.AML.personalInfo.inputs.citizenship.label")}
                        error={errors.citizenship}
                    />
                    <Input
                        type="date"
                        {...register("documentValidUntil")}
                        label={t("verification.AML.personalInfo.inputs.documentValidUntil.label")}
                        error={errors.documentValidUntil}
                        disabled={!!user?.userProfile?.documentValidUntil}
                    />
                    <Input
                        type="text"
                        {...register("documentIssuedBy")}
                        label={t("verification.AML.personalInfo.inputs.documentIssuedBy.label")}
                        error={errors.documentIssuedBy}
                        additionalInfo={t("verification.AML.personalInfo.inputs.documentIssuedBy.additionalInfo")}
                        disabled={!!user?.userProfile?.documentIssuedBy}
                    />
                </div>
            }
        />
    );
};
